import { Component, DestroyRef, computed, HostListener, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { catchError, map, of } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ThemeService } from 'src/app/core/service/theme.service';
import { AuthService } from 'src/app/seguranca/auth.service';
import { GlobalDataService } from 'src/app/shared/services/global-data.service';
import { MensagemService } from 'src/app/shared/services/mensagem.service';
import { FaturaParcela, FaturaUso, obterIdParcela } from 'src/app/view/financeiro/pagamento-page/models/pagamento';
import { PagamentoService } from 'src/app/view/financeiro/pagamento-page/services/pagamento.service';
import { LayoutComponent } from '../layout/layout.component';
import { DashboardNotificacoesService } from 'src/app/dashboard/dashboard-home/service/dashboard-notificacoes.service';
import { SeveridadeAlerta } from 'src/app/dashboard/dashboard-home/model/dashboard-fiscal-api.model';
import {
  DecisaoSolicitacaoRequest,
  NotificacaoRestricao,
  TipoRestricao,
} from 'src/app/view/gerencial/solicitacao-liberacao-page/model/solicitacao-liberacao.model';
import { SolicitacaoLiberacaoService } from 'src/app/view/gerencial/solicitacao-liberacao-page/services/solicitacao-liberacao.service';

const TITULO_RESTRICAO: Record<TipoRestricao, string> = {
  VENDA_CLIENTE_DEVEDOR: 'Liberação — Cliente Devedor',
  VENDA_LIMITE_CREDITO: 'Liberação — Limite de Crédito',
  SANGRIA: 'Liberação — Sangria',
  DESCONTO_EXCEDIDO: 'Liberação — Desconto Excedido',
  CLIENTE_BLOQUEADO: 'Liberação — Cliente Bloqueado',
  CANCELAMENTO_VENDA: 'Liberação — Cancelamento de Venda',
  DESCONTO_ACIMA_LIMITE: 'Liberação — Desconto Acima do Limite',
  ESTOQUE_NEGATIVO: 'Liberação — Estoque Negativo',
  ALTERA_PRECO_VENDA: 'Liberação — Alteração de Preço de Venda',
};

const LABEL_RESTRICAO: Record<TipoRestricao, string> = {
  VENDA_CLIENTE_DEVEDOR: 'Cliente Devedor',
  VENDA_LIMITE_CREDITO: 'Limite de Crédito',
  SANGRIA: 'Sangria',
  DESCONTO_EXCEDIDO: 'Desconto Excedido',
  CLIENTE_BLOQUEADO: 'Cliente Bloqueado',
  CANCELAMENTO_VENDA: 'Cancelamento de Venda',
  DESCONTO_ACIMA_LIMITE: 'Desconto Acima do Limite',
  ESTOQUE_NEGATIVO: 'Estoque Negativo',
  ALTERA_PRECO_VENDA: 'Alteração de Preço de Venda',
};

const ICONE_ALERTA: Record<SeveridadeAlerta, string> = {
  ERROR: 'pi-times-circle',
  WARNING: 'pi-exclamation-triangle',
  INFO: 'pi-info-circle',
};

const FATURA_VAZIA: FaturaUso = { hasPendingPayment: false };

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule],
})
export class ToolbarComponent {
  protected readonly themeService = inject(ThemeService);
  protected readonly notificacoes = inject(DashboardNotificacoesService);

  readonly app = inject(LayoutComponent);

  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly pagamentoService = inject(PagamentoService);
  private readonly globalDataService = inject(GlobalDataService);
  private readonly mensagemService = inject(MensagemService);
  // Exceção de SRP intencional: toolbar expõe dialog de aprovação rápida para que o admin
  // possa decidir sem sair da tela atual — requisito de produto deliberado.
  private readonly solicitacaoService = inject(SolicitacaoLiberacaoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly usuario = this.authService.jwtPayload?.nome ?? 'Usuario';

  readonly fatura = toSignal(
    this.pagamentoService
      .buscarFaturarUso(this.authService.idempresa)
      .pipe(
        map((fatura) => fatura ?? FATURA_VAZIA),
        catchError(() => of(FATURA_VAZIA)),
      ),
    { initialValue: FATURA_VAZIA },
  );

  readonly totalNotificacoes = computed(
    () => this.notificacoes.qtdAlertas() + this.notificacoes.qtdRestricoesPendentes(),
  );

  readonly parcelasPendentes = computed(() => this.fatura().parcelas ?? []);

  readonly showPanel = signal(false);
  readonly showFaturaPanel = signal(false);

  readonly dialogAberto = signal(false);
  private readonly restricaoSelecionada = signal<NotificacaoRestricao | null>(null);
  readonly observacao = signal('');

  readonly tituloDialog = computed(() => {
    const tipo = this.restricaoSelecionada()?.tipo;
    return tipo ? (TITULO_RESTRICAO[tipo] ?? 'Liberação') : 'Liberação';
  });

  labelTipoRestricao(tipo: TipoRestricao): string {
    return LABEL_RESTRICAO[tipo] ?? tipo;
  }

  togglePanel(event: Event): void {
    event.stopPropagation();
    this.showPanel.update((v) => !v);
  }

  toggleFatura(event: Event): void {
    event.stopPropagation();
    const parcelas = this.parcelasPendentes();

    if (parcelas.length > 1) {
      this.showFaturaPanel.update((v) => !v);
      return;
    }

    if (parcelas.length === 1) {
      this.abrirPaginaFatura(parcelas[0]);
    }
  }

  abrirDialogRestricao(restricao: NotificacaoRestricao): void {
    this.showPanel.set(false);
    this.restricaoSelecionada.set(restricao);
    this.observacao.set('');
    this.dialogAberto.set(true);
  }

  confirmarDecisao(acao: 'APROVAR' | 'REJEITAR'): void {
    const restricao = this.restricaoSelecionada();
    if (!restricao) return;

    const req: DecisaoSolicitacaoRequest = {
      observacao: this.observacao() || undefined,
    };

    const chamada$ = acao === 'APROVAR'
      ? this.solicitacaoService.aprovar(restricao.id, req)
      : this.solicitacaoService.rejeitar(restricao.id, req);

    chamada$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.dialogAberto.set(false);
        this.notificacoes.removerRestricaoPendente(restricao.id);
        const msg = acao === 'APROVAR' ? 'Solicitação aprovada com sucesso.' : 'Solicitação rejeitada com sucesso.';
        this.mensagemService.showSuccess(msg);
      },
      error: () => {
        this.mensagemService.showError('Erro ao processar a decisão. Tente novamente.');
      },
    });
  }

  iconeAlerta(severidade: SeveridadeAlerta): string {
    return ICONE_ALERTA[severidade] ?? 'pi-info-circle';
  }

  estaVencida(parcela: FaturaParcela): boolean {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return new Date(parcela.dueDate) < hoje;
  }

  @HostListener('document:click')
  closePanel(): void {
    this.showPanel.set(false);
    this.showFaturaPanel.set(false);
  }

  logout(): void {
    this.authService.logout().then(() => {
      this.globalDataService.clearEmpresa();
      this.router.navigate(['/login']);
    });
  }

  abrirPaginaFatura(parcela: FaturaParcela): void {
    this.showFaturaPanel.set(false);

    const invoiceId = obterIdParcela(parcela);
    const cacheKey = `fatura_${invoiceId}_${Date.now()}`;

    localStorage.setItem(cacheKey, JSON.stringify({ invoiceId, ...parcela }));

    const urlTree = this.router.createUrlTree(['/fatura', invoiceId], {
      queryParams: { cacheKey },
    });

    window.open(this.router.serializeUrl(urlTree), '_blank', 'noopener');
  }
}
